/**
 * Rax Cost Trend Chart
 *
 * Visualizes daily/weekly/monthly cost trends as a bar chart.
 * No external charting library - pure SVG for minimal bundle size.
 */

import { LitElement, html, css, nothing, svg } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { raxApi } from "../../api/rax-api.js";
import { raxTheme } from "../styles/rax-theme.js";

export interface DailyCostData {
  date: string; // YYYY-MM-DD
  costCents: number;
  llmCalls: number;
  toolCalls: number;
}

type TimeRange = "week" | "month" | "3months";

@customElement("rax-cost-trend-chart")
export class RaxCostTrendChart extends LitElement {
  static override styles = [
    raxTheme,
    css`
      :host {
        display: block;
      }
      
      .chart-container {
        padding: var(--space-4);
        background: var(--bg-elevated);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-lg);
      }
      
      .chart-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: var(--space-4);
      }
      
      .chart-title {
        font-size: var(--font-md);
        font-weight: 600;
        color: var(--text-primary);
      }
      
      .range-selector {
        display: flex;
        gap: var(--space-1);
      }
      
      .range-btn {
        padding: var(--space-1) var(--space-2);
        background: var(--bg-secondary);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-sm);
        color: var(--text-secondary);
        font-size: var(--font-xs);
        cursor: pointer;
        transition: all var(--transition-fast);
      }
      
      .range-btn:hover {
        border-color: var(--rax-yellow);
        color: var(--rax-yellow);
      }
      
      .range-btn.active {
        background: var(--rax-yellow);
        border-color: var(--rax-yellow);
        color: var(--rax-black);
      }
      
      .chart-svg {
        width: 100%;
        height: 200px;
      }
      
      .chart-bar {
        fill: var(--rax-yellow);
        opacity: 0.8;
        transition: opacity 0.15s ease;
        cursor: pointer;
      }
      
      .chart-bar:hover {
        opacity: 1;
      }
      
      .chart-axis-line {
        stroke: var(--border-color);
        stroke-width: 1;
      }
      
      .chart-grid-line {
        stroke: var(--border-color);
        stroke-width: 0.5;
        stroke-dasharray: 4 2;
        opacity: 0.5;
      }
      
      .chart-label {
        fill: var(--text-tertiary);
        font-size: 10px;
        font-family: inherit;
      }
      
      .chart-value-label {
        fill: var(--text-secondary);
        font-size: 9px;
        font-family: inherit;
      }
      
      .chart-summary {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
        gap: var(--space-3);
        margin-top: var(--space-4);
        padding-top: var(--space-4);
        border-top: 1px solid var(--border-color);
      }
      
      .summary-item {
        text-align: center;
      }
      
      .summary-value {
        font-size: var(--font-lg);
        font-weight: 700;
        color: var(--rax-yellow);
      }
      
      .summary-label {
        font-size: var(--font-xs);
        color: var(--text-tertiary);
        margin-top: var(--space-1);
      }
      
      .loading {
        text-align: center;
        padding: var(--space-6);
        color: var(--text-tertiary);
      }
      
      .empty-state {
        text-align: center;
        padding: var(--space-6);
        color: var(--text-tertiary);
      }
      
      .tooltip {
        position: absolute;
        padding: var(--space-2) var(--space-3);
        background: var(--bg-primary);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-md);
        font-size: var(--font-xs);
        color: var(--text-primary);
        pointer-events: none;
        z-index: 100;
        white-space: nowrap;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
      }
      
      @media (max-width: 640px) {
        .chart-header {
          flex-direction: column;
          align-items: flex-start;
          gap: var(--space-2);
        }
      
        .chart-svg {
          height: 150px;
        }
      
        .chart-summary {
          grid-template-columns: repeat(2, 1fr);
        }
      }
    `,
  ];

  @property({ type: String })
  userId: string = "";

  @state()
  private data: DailyCostData[] = [];

  @state()
  private loading = true;

  @state()
  private range: TimeRange = "month";

  @state()
  private tooltipData: { x: number; y: number; date: string; cost: number } | null = null;

  override async connectedCallback() {
    super.connectedCallback();
    await this.loadData();
  }

  private async loadData() {
    this.loading = true;
    try {
      const days = this.range === "week" ? 7 : this.range === "month" ? 30 : 90;
      const response = await raxApi.request<{ data: DailyCostData[] }>(
        "GET",
        `/api/rax/usage/daily?days=${days}`,
      );

      if (response.success && response.data?.data) {
        this.data = response.data.data;
      } else {
        // Show empty state - no mock data
        this.data = [];
      }
    } catch (err) {
      console.error("[CostTrendChart] Failed to load data:", err);
      // Show empty state - no mock data
      this.data = [];
    } finally {
      this.loading = false;
    }
  }

  private get totalCents(): number {
    return this.data.reduce((sum, d) => sum + d.costCents, 0);
  }

  private get averageCents(): number {
    if (this.data.length === 0) return 0;
    return this.totalCents / this.data.length;
  }

  private get peakCents(): number {
    return Math.max(...this.data.map((d) => d.costCents), 0);
  }

  private get totalCalls(): number {
    return this.data.reduce((sum, d) => sum + d.llmCalls + d.toolCalls, 0);
  }

  override render() {
    return html`
      <div class="chart-container">
        <div class="chart-header">
          <div class="chart-title">Cost Trend</div>
          <div class="range-selector">
            <button
              class="range-btn ${this.range === "week" ? "active" : ""}"
              @click=${() => this.setRange("week")}
            >
              7D
            </button>
            <button
              class="range-btn ${this.range === "month" ? "active" : ""}"
              @click=${() => this.setRange("month")}
            >
              30D
            </button>
            <button
              class="range-btn ${this.range === "3months" ? "active" : ""}"
              @click=${() => this.setRange("3months")}
            >
              90D
            </button>
          </div>
        </div>

        ${
          this.loading
            ? html`
                <div class="loading">Loading chart data...</div>
              `
            : this.data.length === 0
              ? html`
                  <div class="empty-state">No usage data yet</div>
                `
              : this.renderChart()
        }

        ${
          !this.loading && this.data.length > 0
            ? html`
              <div class="chart-summary">
                <div class="summary-item">
                  <div class="summary-value">$${(this.totalCents / 100).toFixed(2)}</div>
                  <div class="summary-label">Total</div>
                </div>
                <div class="summary-item">
                  <div class="summary-value">$${(this.averageCents / 100).toFixed(2)}</div>
                  <div class="summary-label">Daily Avg</div>
                </div>
                <div class="summary-item">
                  <div class="summary-value">$${(this.peakCents / 100).toFixed(2)}</div>
                  <div class="summary-label">Peak Day</div>
                </div>
                <div class="summary-item">
                  <div class="summary-value">${this.totalCalls}</div>
                  <div class="summary-label">Total Calls</div>
                </div>
              </div>
            `
            : nothing
        }

        ${
          this.tooltipData
            ? html`
              <div
                class="tooltip"
                style="left: ${this.tooltipData.x}px; top: ${this.tooltipData.y}px;"
              >
                ${this.formatDate(this.tooltipData.date)}: $${(this.tooltipData.cost / 100).toFixed(2)}
              </div>
            `
            : nothing
        }
      </div>
    `;
  }

  private renderChart() {
    const width = 600;
    const height = 180;
    const padding = { top: 20, right: 20, bottom: 30, left: 50 };
    const chartWidth = width - padding.left - padding.right;
    const chartHeight = height - padding.top - padding.bottom;

    const maxCost = Math.max(...this.data.map((d) => d.costCents), 1);
    const barWidth = Math.max(2, chartWidth / this.data.length - 2);
    const gap = 2;

    // Generate Y-axis ticks
    const yTicks = [0, maxCost * 0.25, maxCost * 0.5, maxCost * 0.75, maxCost];

    return svg`
      <svg class="chart-svg" viewBox="0 0 ${width} ${height}" preserveAspectRatio="xMidYMid meet">
        <!-- Grid lines -->
        ${yTicks.map((tick) => {
          const y = padding.top + chartHeight - (tick / maxCost) * chartHeight;
          return svg`
            <line
              class="chart-grid-line"
              x1="${padding.left}"
              y1="${y}"
              x2="${width - padding.right}"
              y2="${y}"
            />
          `;
        })}

        <!-- Y-axis -->
        <line
          class="chart-axis-line"
          x1="${padding.left}"
          y1="${padding.top}"
          x2="${padding.left}"
          y2="${height - padding.bottom}"
        />

        <!-- X-axis -->
        <line
          class="chart-axis-line"
          x1="${padding.left}"
          y1="${height - padding.bottom}"
          x2="${width - padding.right}"
          y2="${height - padding.bottom}"
        />

        <!-- Y-axis labels -->
        ${yTicks.map((tick) => {
          const y = padding.top + chartHeight - (tick / maxCost) * chartHeight;
          return svg`
            <text
              class="chart-value-label"
              x="${padding.left - 5}"
              y="${y + 3}"
              text-anchor="end"
            >
              $${(tick / 100).toFixed(2)}
            </text>
          `;
        })}

        <!-- Bars -->
        ${this.data.map((d, i) => {
          const barHeight = (d.costCents / maxCost) * chartHeight;
          const x = padding.left + i * (barWidth + gap);
          const y = padding.top + chartHeight - barHeight;

          return svg`
            <rect
              class="chart-bar"
              x="${x}"
              y="${y}"
              width="${barWidth}"
              height="${barHeight}"
              rx="2"
              @mouseenter=${(e: MouseEvent) => this.showTooltip(e, d)}
              @mouseleave=${() => this.hideTooltip()}
            />
          `;
        })}

        <!-- X-axis labels (show a subset for readability) -->
        ${this.data
          .filter((_, i) => {
            const interval = Math.ceil(this.data.length / 6);
            return i % interval === 0 || i === this.data.length - 1;
          })
          .map((d, _, filtered) => {
            const i = this.data.indexOf(d);
            const x = padding.left + i * (barWidth + gap) + barWidth / 2;
            return svg`
            <text
              class="chart-label"
              x="${x}"
              y="${height - 8}"
              text-anchor="middle"
            >
              ${this.formatShortDate(d.date)}
            </text>
          `;
          })}
      </svg>
    `;
  }

  private setRange(range: TimeRange) {
    this.range = range;
    this.loadData();
  }

  private showTooltip(e: MouseEvent, data: DailyCostData) {
    const rect = this.getBoundingClientRect();
    this.tooltipData = {
      x: e.clientX - rect.left + 10,
      y: e.clientY - rect.top - 30,
      date: data.date,
      cost: data.costCents,
    };
  }

  private hideTooltip() {
    this.tooltipData = null;
  }

  private formatDate(dateStr: string): string {
    const date = new Date(dateStr + "T00:00:00");
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  }

  private formatShortDate(dateStr: string): string {
    const date = new Date(dateStr + "T00:00:00");
    return date.toLocaleDateString("en-US", { month: "numeric", day: "numeric" });
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "rax-cost-trend-chart": RaxCostTrendChart;
  }
}
